import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Modal, Space, Button, Progress, notification, Spin } from 'antd'
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined'
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled'
import classNames from 'classnames'

import { getUUID } from 'gyl-common/utils/uuid'
import { delay } from 'gyl-common/utils/timer'
import VirtualTable from 'gyl-common/components/VirtualTable'
import styles from './index.less'

import { K, M, G } from '../../constants/file'

// 检测是否完成
export const checkDone = file => {
    return ['done', 'error'].includes(file.status)
}

const Status = ({ status, percent, message, task }) => {
    const onStop = () => {
        task.stop && task.stop()
    }

    const onStart = () => {
        task.start && task.start()
    }

    const onCancel = () => {
        task.cancel && task.cancel()
    }

    switch (status) {
        case 'done':
            return (
                <div className={classNames(styles.status, styles.hasSuccess)}>
                    <CheckCircleFilled className={styles.icon} />
                    <span className={styles.label}>下载成功</span>
                </div>
            )
        case 'error':
            return (
                <div className={classNames(styles.status, styles.hasFail)}>
                    <CloseCircleFilled className={styles.icon} />
                    <span className={styles.label}>下载失败</span>
                    {message ? <span className={styles.tip}>原因：{message}</span> : null}
                </div>
            )
        default:
            return (
                <div className={styles.status}>
                    <Progress
                        className={styles.progress}
                        percent={parseInt(percent)}
                        size="small"
                        strokeWidth={8}
                        strokeColor="#00B42A"
                    />

                    <Space className={styles.operations}>
                        {status === 'stop' ? <a onClick={onStart}>开始</a> : <a onClick={onStop}>暂停</a>}

                        <a onClick={onCancel}>取消</a>
                    </Space>
                </div>
            )
    }
}

const Name = ({ name }: any) => {
    const dotIndex = name.lastIndexOf('.')
    const pureName = name.slice(0, dotIndex)
    const suffix = name.slice(dotIndex)

    return (
        <div className={styles.name}>
            <span className={styles.lt}>{pureName}</span>
            <span>{suffix}</span>
        </div>
    )
}

// 格式化单位
const formatSize = size => {
    if (size >= G) {
        return `${Number((size / G).toFixed(2))}GB`
    }

    if (size >= M) {
        return `${Number((size / M).toFixed(2))}MB`
    }

    if (size >= K) {
        return `${Number((size / K).toFixed(2))}KB`
    }

    return `${size}B`
}

const DEFAULT_IMG =
    'https://images.sursung.com/dis/front/D211EB3BF85B4906A92B31D755A503B21655454319370.png?x-oss-process=image/resize,m_fill,w_84,h_84'

const Thumbnail = ({ url }) => {
    return (
        <div className={styles.thumbnailWrap}>
            <img className={styles.thumbnail} src={url || DEFAULT_IMG} alt="" width={32} height={32} />
        </div>
    )
}

const Content = ({ files }) => {
    const columns = [
        {
            title: '文件',
            dataIndex: 'thumbnail',
            width: 88,
            align: 'center',
            shouldCellUpdate(record, prevRecord) {
                return record.url !== prevRecord.url
            },
            render() {
                return <Thumbnail />
            },
        },
        {
            title: '名称',
            dataIndex: 'name',
            width: 250,
            ellipsis: true,
            shouldCellUpdate(record, prevRecord) {
                return record.name !== prevRecord.name
            },
            render(_, record): any {
                return <Name name={record.name} />
            },
        },
        {
            title: '大小',
            dataIndex: 'size',
            width: 160,
            align: 'center',
            shouldCellUpdate(record, prevRecord) {
                return record.size !== prevRecord.size
            },
            render(_, record) {
                return formatSize(record.size)
            },
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 416,
            shouldCellUpdate(record, prevRecord) {
                return (
                    record.status !== prevRecord.status ||
                    record.message !== prevRecord.message ||
                    record.percent !== prevRecord.percent
                )
            },
            render(_, record) {
                return (
                    <Status
                        status={record.status}
                        message={record.message}
                        percent={record.percent}
                        task={record.task}
                    />
                )
            },
        },
    ]

    return <VirtualTable className={styles.table} rowKey="uid" size="small" columns={columns} dataSource={files} />
}

const calNum = files => {
    return files.reduce(
        (t, c) => {
            switch (c.status) {
                case 'done':
                    t.success += 1
                    break
                case 'error':
                    t.fail += 1
                    break
                default:
                    t.process += 1
            }

            return t
        },
        {
            success: 0,
            process: 0,
            fail: 0,
            total: files.length,
        },
    )
}

const Title = ({ numObj }: any) => {
    const obj = {
        icon: null,
        message: '',
    }

    const render = () => {
        return (
            <div className={styles.title}>
                <div className={styles.iconWrap}>{obj.icon}</div>
                <div className={styles.text}>{obj.message}</div>
            </div>
        )
    }

    const hasProcess = numObj.process <= 0

    // 全部下载成功
    if (numObj.success >= numObj.total && hasProcess) {
        Object.assign(obj, {
            icon: <CheckCircleFilled className={classNames(styles.icon, styles.hasSuccess)} />,
            message: '文件下载成功',
        })

        return render()
    }

    // 全部下载失败
    if (numObj.fail >= numObj.total && hasProcess) {
        Object.assign(obj, {
            icon: <CloseCircleFilled className={classNames(styles.icon, styles.hasFail)} />,
            message: '文件下载失败',
        })

        return render()
    }

    // 部分下载成功
    if (numObj.success > 0 && hasProcess) {
        Object.assign(obj, {
            icon: <InfoCircleFilled className={classNames(styles.icon, styles.hasPartSuccess)} />,
            message: '部分文件下载成功',
        })

        return render()
    }

    Object.assign(obj, {
        icon: (
            <Spin
                size="small"
                spinning
                indicator={<LoadingOutlined className={classNames(styles.icon, styles.hasLoading)} spin />}
            />
        ),
        message: '文件下载中...',
    })

    return render()
}

const Info = ({ numObj }) => {
    return (
        <Space className={styles.info} size={25}>
            <span>
                总数：<span className={styles.num}>{numObj.total}</span>个
            </span>
            <span>
                成功：
                <span
                    className={classNames(styles.num, {
                        [styles.hasSuccess]: numObj.success > 0,
                    })}
                >
                    {numObj.success}
                </span>
                个
            </span>
            <span>
                失败：
                <span
                    className={classNames(styles.num, {
                        [styles.hasFail]: numObj.fail > 0,
                    })}
                >
                    {numObj.fail}
                </span>
                个
            </span>
        </Space>
    )
}

const openNotification = ({ key, files, onShow, onClose }: any) => {
    const numObj = calNum(files)

    const message = <span className={styles.noticeMessage}>{numObj.process !== 0 ? '下载中...' : '下载结果'}</span>

    const description = (
        <div className={styles.description}>
            <a className={classNames('ant-notification-notice-close', styles.hasShow)} onClick={onClose}>
                <CloseOutlined />
            </a>
            <Title numObj={numObj} />
            <Info numObj={numObj} />
        </div>
    )

    notification.open({
        key,
        className: styles.notification,
        duration: null,
        closeIcon: null,
        message,
        description,
        placement: 'bottomRight',
        btn: <a onClick={onShow}>查看详情</a>,
    })
}

export default ({ visible, onRef, onDownloadCancel, afterClose, afterDownloadFinish }: any) => {
    const [files, setFiles] = useState([])
    const [hide, setHide] = useState(false)
    const uidRef: any = useRef(null)
    const closedRef = useRef(false)
    const confirmRef = useRef(false)

    if (!uidRef.current) {
        uidRef.current = getUUID()
    }

    onRef &&
        onRef({
            setFiles(e) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setFiles(e)
                })
            },
        })

    const onShow = () => {
        notification.close(uidRef.current)
        setHide(false)
    }

    const onHide = () => {
        setHide(true)
    }

    const onClose = async () => {
        if (confirmRef.current) {
            return
        }
        confirmRef.current = true

        await delay(150)

        const hasProcessing = files.some(v => !checkDone(v))

        if (hasProcessing) {
            Modal.confirm({
                title: '提示',
                zIndex: 1200,
                icon: <ExclamationCircleOutlined />,
                content: '确定要取消所有文件的下载吗？',
                okText: '确认',
                cancelText: '取消',
                onOk() {
                    closedRef.current = true
                    notification.close(uidRef.current)
                    setTimeout(() => {
                        onDownloadCancel && onDownloadCancel()
                    }, 0)
                },
                afterClose() {
                    confirmRef.current = false
                },
            })
        } else {
            confirmRef.current = false
            closedRef.current = true
            notification.close(uidRef.current)
            onDownloadCancel && onDownloadCancel()
        }
    }

    const hasProcessing = files.some(v => !checkDone(v))

    const footer = (
        <Space>
            {hasProcessing ? <Button onClick={onClose}>取消</Button> : null}
            {hasProcessing ? (
                <Button type="primary" onClick={onHide}>
                    隐藏
                </Button>
            ) : (
                <Button type="primary" onClick={onClose}>
                    完成
                </Button>
            )}
        </Space>
    )

    const title = useMemo(() => {
        const doneFiles = files.filter((v: any) => checkDone(v))

        if (files.length && doneFiles.length === files.length) {
            return '下载结果'
        }

        return `下载文件...（${doneFiles.length}/${files.length}）`
    }, [files])

    useEffect(() => {
        if (!hide) {
            notification.close(uidRef.current)
            return
        }

        if (closedRef.current) {
            notification.close(uidRef.current)
            return
        }

        openNotification({
            key: uidRef.current,
            files,
            onShow,
            onClose,
        })
    }, [hide, files])

    useEffect(() => {
        const doneFiles = files.filter((v: any) => checkDone(v))

        if (files.length && doneFiles.length === files.length) {
            afterDownloadFinish && afterDownloadFinish()
        }
    }, [files])

    useEffect(() => {
        return () => {
            notification.close(uidRef.current)
        }
    }, [])

    if (hide) {
        return null
    }

    return (
        <Modal
            maskClosable={false}
            keyboard={false}
            title={title}
            width={960}
            zIndex={1100}
            visible={visible}
            footer={footer}
            onCancel={onClose}
            afterClose={afterClose}
        >
            <Content files={files} />
        </Modal>
    )
}
